import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { API_URL } from '../../lib/constants';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';

const GOAL_TYPES = ['behavioral', 'cognitive', 'affective', 'relational', 'transformational'];
const TIME_HORIZONS = ['single_chat', '30_days', '90_days', 'ongoing'] as const;

const METHODOLOGY_STYLES = ['socratic', 'didactic', 'narrative', 'exemplary', 'counseling'];
const METHODOLOGY_ANCHOR_SOURCES = ['scripture', 'founder_texts', 'personal_history', 'medical_lit', 'none'];
const METHODOLOGY_PACINGS = ['gradual', 'direct', 'responsive_only'];
const METHODOLOGY_VOICES = ['companion', 'mentor', 'teacher', 'peer', 'pastor'];
const METHODOLOGY_QUESTION_DENSITIES = ['high', 'medium', 'low'];
const METHODOLOGY_PERSONALIZATION_DEPTHS = ['none', 'low', 'medium', 'high'];

interface CognitoUser {
  sub: string;
  email: string;
  username: string;
}

interface MethodologyPresetOption {
  id: string;
  name: string;
  methodology: Record<string, string>;
}

interface PersonaOption {
  id: string;
  name: string;
  goalIds?: string[];
}

const defaultMethodology = () => ({
  style: 'socratic',
  anchorSource: 'scripture',
  pacing: 'responsive_only',
  voice: 'companion',
  questionDensity: 'high',
  personalizationDepth: 'medium',
});

const emptyForm = () => ({
  id: '',
  scope: 'org',
  owner: 'chip' as string | string[],
  name: '',
  alignmentStatement: '',
  goalTypes: ['transformational'],
  timeHorizon: '90_days',
  initiativeLevel: 'companion',
  priorityWeight: 0.3,
  userVisibility: 'full',
  lifecycleStatus: 'active',
  methodologyPresetId: '' as string,
  methodology: defaultMethodology(),
});

function goalToForm(goal: Record<string, unknown>) {
  return {
    ...emptyForm(),
    ...goal,
    methodologyPresetId: (goal.methodologyPresetId as string) ?? '',
    methodology: {
      ...defaultMethodology(),
      ...(goal.methodology as object | undefined),
    },
  };
}

function normalizeOwner(owner: unknown): string | string[] {
  if (Array.isArray(owner)) return owner;
  if (typeof owner === 'string') return owner;
  return '';
}

export default function GoalLibraryPage() {
  const { userInfo } = useUserInfoContext();
  const [goals, setGoals] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());

  const [cognitoUsers, setCognitoUsers] = useState<CognitoUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [presets, setPresets] = useState<MethodologyPresetOption[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);

  const [personas, setPersonas] = useState<PersonaOption[]>([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  const [personaIds, setPersonaIds] = useState<string[]>([]);

  const authHeaders = {
    Authorization: `Bearer ${userInfo?.accessToken}`,
  };
  const jsonHeaders = {
    ...authHeaders,
    'Content-Type': 'application/json',
  };

  const load = () => {
    if (!userInfo?.accessToken) return;
    fetch(`${API_URL}/goals`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data) => setGoals(Array.isArray(data) ? data : []));
  };

  const loadPresets = () => {
    if (!userInfo?.accessToken) return;
    setPresetsLoading(true);
    fetch(`${API_URL}/methodology-presets`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data) => setPresets(Array.isArray(data) ? data : []))
      .catch((e) => console.error('Failed to load methodology presets', e))
      .finally(() => setPresetsLoading(false));
  };

  const loadCognitoUsers = () => {
    if (!userInfo?.accessToken || cognitoUsers.length > 0) return;
    setUsersLoading(true);
    fetch(`${API_URL}/admin/cognito-users`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data) => setCognitoUsers(Array.isArray(data.users) ? data.users : []))
      .catch((e) => console.error('Failed to load Cognito users', e))
      .finally(() => setUsersLoading(false));
  };

  const loadPersonas = () => {
    if (!userInfo?.accessToken) return;
    setPersonasLoading(true);
    fetch(`${API_URL}/personas`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data) => setPersonas(Array.isArray(data) ? data : []))
      .catch((e) => console.error('Failed to load personas', e))
      .finally(() => setPersonasLoading(false));
  };

  useEffect(() => {
    load();
    loadPresets();
    loadPersonas();
  }, [userInfo?.accessToken]);

  const handleScopeChange = (newScope: string) => {
    if (newScope === 'user') {
      loadCognitoUsers();
      setForm({ ...form, scope: newScope, owner: [] });
    } else {
      setForm({ ...form, scope: newScope, owner: 'chip' });
    }
  };

  const handlePresetChange = (presetId: string) => {
    if (!presetId) {
      setForm({ ...form, methodologyPresetId: '', methodology: defaultMethodology() });
      return;
    }
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      setForm({
        ...form,
        methodologyPresetId: presetId,
        methodology: { ...defaultMethodology(), ...preset.methodology },
      });
    }
  };

  const startCreate = () => {
    setSelectedId(null);
    setForm(emptyForm());
    setPersonaIds([]);
  };

  const startEdit = (goal: any) => {
    setSelectedId(goal.id);
    const f = goalToForm(goal);
    f.owner = normalizeOwner(goal.owner);
    if (f.scope === 'user') loadCognitoUsers();
    setForm(f);
    fetch(`${API_URL}/goals/${goal.id}/personas`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data) => setPersonaIds(Array.isArray(data.personaIds) ? data.personaIds : []))
      .catch(() => setPersonaIds([]));
  };

  const save = async () => {
    const isNew = !selectedId;
    const url = isNew ? `${API_URL}/goals` : `${API_URL}/goals/${selectedId}`;
    const method = isNew ? 'POST' : 'PUT';
    const payload = {
      ...form,
      ...(isNew && form.id?.trim() ? { id: form.id.trim() } : {}),
      ...(isNew && !form.id?.trim() ? { id: undefined } : {}),
      methodologyPresetId: form.methodologyPresetId || undefined,
    };
    const res = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(payload) });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || 'Save failed');
      return;
    }
    const saved = await res.json();
    // Sync persona attachment for both scopes. For user-scoped goals the persona set
    // narrows where the goal applies; owner filtering (backend) limits it to the assigned users.
    const personaRes = await fetch(`${API_URL}/goals/${saved.id}/personas`, {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify({ personaIds }),
    });
    if (!personaRes.ok) {
      const err = await personaRes.json().catch(() => ({}));
      alert(err.error || 'Goal saved, but updating attached personas failed');
    }
    load();
    loadPersonas();
    startEdit(saved);
  };

  const remove = async (goalId: string, goalName?: string) => {
    const label = goalName ? `"${goalName}"` : 'this goal';
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) {
      return;
    }
    const res = await fetch(`${API_URL}/goals/${goalId}`, { method: 'DELETE', headers: authHeaders });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || 'Delete failed');
      return;
    }
    if (selectedId === goalId) {
      startCreate();
    }
    load();
  };

  const pause = async () => {
    if (!selectedId) return;
    const res = await fetch(`${API_URL}/goals/${selectedId}/pause`, { method: 'POST', headers: authHeaders });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || 'Pause failed');
      return;
    }
    const updated = await res.json();
    load();
    startEdit(updated);
  };

  const selectedUserSubs = Array.isArray(form.owner) ? form.owner : [];
  const selectedCognitoUsers = cognitoUsers.filter((u) => selectedUserSubs.includes(u.sub));
  const selectedPersonas = personas.filter((p) => personaIds.includes(p.id));
  const usingPreset = Boolean(form.methodologyPresetId);
  const linkedPreset = presets.find((p) => p.id === form.methodologyPresetId);

  return (
    <Box sx={{ p: 2, display: 'flex', gap: 2, height: '100%' }}>
      <Paper sx={{ width: 360, p: 2, overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>Goal Library</Typography>
        <Button fullWidth variant="outlined" sx={{ mb: 2 }} onClick={startCreate}>
          New goal
        </Button>
        {goals.map((g) => (
          <Box
            key={g.id}
            sx={{
              p: 1,
              mb: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5,
              bgcolor: selectedId === g.id ? 'action.selected' : 'transparent',
              borderRadius: 1,
            }}
          >
            <Box
              sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
              onClick={() => startEdit(g)}
            >
              <Typography variant="subtitle2" noWrap>{g.name}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                <Chip size="small" label={g.scope} />
                <Chip size="small" label={g.lifecycleStatus} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <Tooltip title="Edit">
                <IconButton size="small" aria-label={`Edit ${g.name}`} onClick={() => startEdit(g)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`Delete ${g.name}`}
                  onClick={() => remove(g.id, g.name)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Paper>
      <Paper sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>{selectedId ? 'Edit goal' : 'Create goal'}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="ID (optional on create)"
              fullWidth
              value={form.id}
              disabled={Boolean(selectedId)}
              helperText={selectedId ? 'ID cannot be changed after create' : undefined}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Name" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Alignment statement" fullWidth multiline minRows={2} value={form.alignmentStatement} onChange={(e) => setForm({ ...form, alignmentStatement: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Scope</InputLabel>
              <Select
                label="Scope"
                value={form.scope}
                onChange={(e) => handleScopeChange(e.target.value)}
              >
                <MenuItem value="org">org (all users)</MenuItem>
                <MenuItem value="user">user (specific users)</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              org: applies to every user of the attached personas. user: applies only to the selected users.
            </Typography>
          </Grid>

          <Grid item xs={12} md={9}>
            <Autocomplete
              multiple
              options={personas}
              disableCloseOnSelect
              loading={personasLoading}
              value={selectedPersonas}
              getOptionLabel={(option) => option.name || option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_e, newValue) => setPersonaIds(newValue.map((p) => p.id))}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  <Box>
                    <Typography variant="body2">{option.name || option.id}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.id}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Attach to personas"
                  placeholder="Search personas..."
                  helperText={
                    form.scope === 'org'
                      ? 'Every user of the selected personas is affected by this goal.'
                      : 'This goal applies only when the assigned users below talk to the selected personas.'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {personasLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {form.scope === 'user' && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={cognitoUsers}
                disableCloseOnSelect
                loading={usersLoading}
                value={selectedCognitoUsers}
                getOptionLabel={(option) => option.email || option.username || option.sub}
                isOptionEqualToValue={(option, value) => option.sub === value.sub}
                onChange={(_e, newValue) => {
                  setForm({ ...form, owner: newValue.map((u) => u.sub) });
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.sub}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <Box>
                      <Typography variant="body2">{option.email || option.username}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.sub}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign to users"
                    placeholder="Search users..."
                    helperText="Only these users get this goal (on the personas selected above)."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Time horizon</InputLabel>
              <Select
                label="Time horizon"
                value={form.timeHorizon}
                onChange={(e) => setForm({ ...form, timeHorizon: e.target.value })}
              >
                {TIME_HORIZONS.map((h) => (
                  <MenuItem key={h} value={h}>{h.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              session: resolve in this chat; 30/90 day: one layer per turn across weeks; long arc: plant seeds patiently
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Initiative</InputLabel>
              <Select label="Initiative" value={form.initiativeLevel} onChange={(e) => setForm({ ...form, initiativeLevel: e.target.value })}>
                <MenuItem value="companion">companion</MenuItem>
                <MenuItem value="responsive">responsive</MenuItem>
                <MenuItem value="active">active</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              companion: never raises proactively; responsive: raises on user opening (1/session); active: steers back on drift (up to 3/session)
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField label="Priority weight (0-1)" type="number" fullWidth inputProps={{ min: 0, max: 1, step: 0.1 }} value={form.priorityWeight} onChange={(e) => setForm({ ...form, priorityWeight: Number(e.target.value) })} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Goal types</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {GOAL_TYPES.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  color={form.goalTypes?.includes(t) ? 'primary' : 'default'}
                  onClick={() => {
                    const set = new Set(form.goalTypes || []);
                    if (set.has(t)) set.delete(t);
                    else set.add(t);
                    setForm({ ...form, goalTypes: [...set] });
                  }}
                />
              ))}
            </Box>
          </Grid>

          {/* Methodology section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>Methodology</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Methodology preset</InputLabel>
              <Select
                label="Methodology preset"
                value={form.methodologyPresetId}
                onChange={(e) => handlePresetChange(e.target.value)}
              >
                <MenuItem value="">
                  <em>Custom (manual)</em>
                </MenuItem>
                {presetsLoading && (
                  <MenuItem disabled>Loading presets...</MenuItem>
                )}
                {presets.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            {usingPreset && linkedPreset && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Methodology fields are set by preset <strong>{linkedPreset.name}</strong>. Change the preset or select "Custom" to edit manually.
              </Alert>
            )}
            {usingPreset && !linkedPreset && form.methodologyPresetId && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                Linked preset <code>{form.methodologyPresetId}</code> not found. Values shown are from the goal's snapshot.
              </Alert>
            )}
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" disabled={usingPreset}>
              <InputLabel>Style</InputLabel>
              <Select label="Style" value={form.methodology.style} onChange={(e) => setForm({ ...form, methodology: { ...form.methodology, style: e.target.value } })}>
                {METHODOLOGY_STYLES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" disabled={usingPreset}>
              <InputLabel>Anchor</InputLabel>
              <Select label="Anchor" value={form.methodology.anchorSource ?? 'none'} onChange={(e) => setForm({ ...form, methodology: { ...form.methodology, anchorSource: e.target.value } })}>
                {METHODOLOGY_ANCHOR_SOURCES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" disabled={usingPreset}>
              <InputLabel>Pacing</InputLabel>
              <Select label="Pacing" value={form.methodology.pacing} onChange={(e) => setForm({ ...form, methodology: { ...form.methodology, pacing: e.target.value } })}>
                {METHODOLOGY_PACINGS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" disabled={usingPreset}>
              <InputLabel>Voice</InputLabel>
              <Select label="Voice" value={form.methodology.voice} onChange={(e) => setForm({ ...form, methodology: { ...form.methodology, voice: e.target.value } })}>
                {METHODOLOGY_VOICES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" disabled={usingPreset}>
              <InputLabel>Questions</InputLabel>
              <Select label="Questions" value={form.methodology.questionDensity} onChange={(e) => setForm({ ...form, methodology: { ...form.methodology, questionDensity: e.target.value } })}>
                {METHODOLOGY_QUESTION_DENSITIES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" disabled={usingPreset}>
              <InputLabel>Personalization</InputLabel>
              <Select label="Personalization" value={form.methodology.personalizationDepth} onChange={(e) => setForm({ ...form, methodology: { ...form.methodology, personalizationDepth: e.target.value } })}>
                {METHODOLOGY_PERSONALIZATION_DEPTHS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="contained" onClick={save}>
            {selectedId ? 'Save changes' : 'Create goal'}
          </Button>
          {selectedId && (
            <>
              <Button variant="outlined" onClick={startCreate}>Cancel</Button>
              <Button variant="outlined" onClick={pause}>Pause</Button>
              <Button variant="outlined" color="error" onClick={() => remove(selectedId, form.name)}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
